import { useCallback, useEffect, useState } from 'react';
import { CanvasRoot } from '@/r3f/CanvasRoot';
import { LobbyScene } from '@/scenes/LobbyScene';
import { Home } from '@/ui/Home';
import { Settings } from '@/ui/Settings';
import { Shop } from '@/ui/Shop';
import { Inventory } from '@/ui/Inventory';
import { GameLoader } from '@/ui/GameLoader';
import { InteractionPrompt } from '@/ui/economy/InteractionPrompt';
import { BuildingPanel } from '@/ui/economy/BuildingPanel';
import { GameHUD } from '@/ui/economy/GameHUD';
import { CargoPanel } from '@/ui/economy/CargoPanel';
import { useUIStore } from '@/state/useUIStore';
import { ShipHUD } from '@/ui/ShipHUD';
import { MountHUD } from '@/ui/MountHUD';
import { MountPrompt } from '@/ui/MountPrompt';
import { CombatHUD } from '@/ui/CombatHUD';
import { InventoryHUD } from '@/ui/InventoryHUD';
import { Compass } from '@/ui/Compass';
import './App.css';

export function App() {
  const screen = useUIStore((s) => s.screen);
  const showSettings = useUIStore((s) => s.showSettings);
  const closeSettings = useUIStore((s) => s.closeSettings);
  const setScreen = useUIStore((s) => s.setScreen);
  const setLoadProgress = useUIStore((s) => s.setLoadProgress);
  const [sceneReady, setSceneReady] = useState(false);

  // Start on home screen and dismiss the HTML loading overlay
  useEffect(() => {
    if (screen === 'loading') {
      setScreen('home');
      const el = document.getElementById('loading-screen');
      if (el) {
        el.classList.add('fade-out');
        setTimeout(() => el.remove(), 800);
      }
    }
  }, [screen, setScreen]);

  // Reset scene ready and loading progress when returning to home
  useEffect(() => {
    if (screen === 'home') {
      setSceneReady(false);
      setLoadProgress(0, 'Initializing...');
    }
  }, [screen, setLoadProgress]);

  const showInventory = useUIStore((s) => s.showInventory);
  const closeInventory = useUIStore((s) => s.closeInventory);

  // Global ESC key handler
  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (showInventory) { closeInventory(); return; }
    if (showSettings) { closeSettings(); return; }
  }, [showSettings, closeSettings, showInventory, closeInventory]);

  useEffect(() => {
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [handleEsc]);

  // Home screen — single unified UI
  if (screen === 'home') {
    return (
      <div className="app-root">
        <Home />
        <Settings />
        <Shop />
        <Inventory />
      </div>
    );
  }

  // In-game screens — load the 3D world
  return (
    <div className="app-root">
      <CanvasRoot>
        <LobbyScene onReady={() => setSceneReady(true)} />
      </CanvasRoot>

      <GameLoader sceneReady={sceneReady} />

      {/* In-game HUD: settings (top-right) */}
      <GameHUD />

      {/* Compass minimap (top-center) */}
      <Compass />

      {/* Economy interaction UI */}
      <InteractionPrompt />
      <BuildingPanel />

      {/* Ship controls HUD (when aboard) */}
      <ShipHUD />

      {/* Mount controls HUD (when riding horse) */}
      <MountHUD />
      <MountPrompt />

      {/* Combat HUD (when on foot) */}
      <CombatHUD />

      {/* Inventory HUD (bottom-left, when on foot) */}
      <InventoryHUD />

      {/* In-game cargo inventory (I key) */}
      <CargoPanel />

      {/* Overlay modals */}
      <Settings />
    </div>
  );
}

export default App;
